import { AbstractControl, FormArray, FormControl, FormGroup, UntypedFormArray, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { EventEmitter } from '@angular/core';

export type ControlsOf<T extends Record<string, any>> = {
  [K in keyof T]: T[K] extends Record<any, any>
    ? FormGroup<ControlsOf<T[K]>>
    : FormControl<T[K]>;
};

export function getFormGroup<T extends Record<string, any>>(data: T | { [key: string]: any } | { [key: string]: AbstractControl<T> }): FormGroup<ControlsOf<T>> | FormArray<FormGroup<ControlsOf<T>> | FormControl<T>> {
  const form = {};
  for (const [key, value] of Object.entries(data)) {
    if (value === null || value === undefined || typeof value.getMonth === 'function') {
      (form as any)[key] = new UntypedFormControl(value);
    }
    else {
      switch (typeof value) {
        case 'string':
          (form as any)[key] = new FormControl<string>(value);
          break;
        case 'number':
          (form as any)[key] = new FormControl<number>(value);
          break;
        case 'boolean':
          (form as any)[key] = new FormControl<boolean>(value);
          break;
        case 'object':
          (form as any)[key] = getFormGroup(value);
          break;
      }
    }
  }
  return (data instanceof Array) ? new FormArray<FormGroup<ControlsOf<T>> | FormControl<T>>(Object.values(form)) : new FormGroup<ControlsOf<T>>(form as any);
}


export function logInvalidFormFields(parent: UntypedFormGroup | UntypedFormArray, detailed: boolean = false, combinedKey: string = ''): void {
  for (const key of Object.keys(parent.controls)) {
    const control = (parent.controls as any)[key];
    if (control instanceof UntypedFormArray) {
      if (control.invalid) {
        console.log(`FormArray [${combinedKey}${key}] is invalid`, detailed ? control.value : null, detailed ? control.errors : null);
      }
      logInvalidFormFields(control, detailed, `${combinedKey}${key}.`);
    }
    else if (control instanceof UntypedFormGroup) {
      if (control.invalid) {
        console.log(`FormGroup [${combinedKey}${key}] is invalid`, detailed ? control.value : null, detailed ? control.errors : null);
      }
      logInvalidFormFields(control, detailed, `${combinedKey}${key}.`);
    }
    else if (control.invalid) {
      console.log(`Control [${combinedKey}${key}] is invalid`, detailed ? control.value : null, detailed ? control.errors : null);
    }
  }
}

export function logDirtyControls(parent: UntypedFormGroup | UntypedFormArray, detailed: boolean = false, combinedKey: string = ''): void {
  for (const key of Object.keys(parent.controls)) {
    const control = (parent.controls as any)[key];
    if (control instanceof UntypedFormArray) {
      if (control.dirty) {
        console.log(`FormArray [${combinedKey}${key}] is dirty`, detailed ? control.value : null, detailed ? control.errors : null);
      }
      logDirtyControls(control, detailed, `${combinedKey}${key}.`);
    }
    else if (control instanceof UntypedFormGroup) {
      if (control.dirty) {
        console.log(`FormGroup [${combinedKey}${key}] is dirty`, detailed ? control.value : null, detailed ? control.errors : null);
      }
      logDirtyControls(control, detailed, `${combinedKey}${key}.`);
    }
    else if (control.dirty) {
      console.log(`Control [${combinedKey}${key}] is dirty`, detailed ? control.value : null, detailed ? control.errors : null);
    }
  }
}

// TODO remove? there is a markAllAsTouched on formgroups
export function markAsTouchedWithChildren(control: AbstractControl): void {
  if (control instanceof UntypedFormControl) {
    (<UntypedFormControl>control).markAsTouched({ onlySelf: true });
    (control.statusChanges as EventEmitter<any>).emit('TOUCHED');
  }
  else if (control instanceof UntypedFormArray) {
    const formArray = <UntypedFormArray>control;
    formArray.markAsTouched({ onlySelf: true });
    (control.statusChanges as EventEmitter<any>).emit('TOUCHED');
    for (let i = 0; i < formArray.length; i++) {
      markAsTouchedWithChildren(formArray.at(i));
    }
  }
  else if (control instanceof UntypedFormGroup) {
    const formGroup = <UntypedFormGroup>control;
    formGroup.markAsTouched({ onlySelf: true });
    Object.keys(formGroup.controls).forEach((key) => {
      const subControl = formGroup.get(key);
      markAsTouchedWithChildren(<UntypedFormControl>subControl);
    });
  }
}

export function removeFormControlError(control: AbstractControl, errorkey: string): void {
  const err = control.errors; // get control errors
  if (err) {
    delete err[errorkey]; // delete your own error
    if (!Object.keys(err).length) { // if no errors left
      control.setErrors(null); // set control errors to null making it VALID
    }
    else {
      control.setErrors(err); // controls got other errors so set them back
    }
  }
}
